import { type Breakpoint, breakpoints } from '@ctw/config/zui';
import { debounce } from 'lodash-es';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

export interface BreakpointQueries {
  isAtLeast: Record<Breakpoint, boolean>;
  isAtMost: Record<Breakpoint, boolean>;
  is: Record<Breakpoint, boolean>;
}

interface UseContainerQueryResult {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  breakpoints: BreakpointQueries;
}

interface UseContainerQueryOptions {
  containerRef: RefObject<HTMLElement | null>;
}

/*
 * Given a ref, this hook provides the current height and width of the element and the result of min
 * and max breakpoint queries across our breakpoints. Note that by default the max queries are
 * inclusive of the upper bounds of the breakpoint's range.
 */
export const useContainerQuery = ({
  containerRef,
}: UseContainerQueryOptions): UseContainerQueryResult => {
  const animationFrameRequest = useRef<number | null>(null);
  const [initialMountCheckComplete, setInitialMountCheckComplete] = useState(false);
  const [containerSize, setContainerSize] = useState({
    // Set fallback widths to `1` instead of `0`, because consumers often use this hook to alter
    // layout properties or determine if something should be displayed with a `<= 0` or `>= 0` check.
    // If initial values are `0` and conditionalized styling results in the `containerRef` element
    // having no height or width (such as conditionally applying `hidden`), then this hook would
    // always report the height and width as `0` even when the element has content.
    width: containerRef.current?.clientWidth ?? 1,
    height: containerRef.current?.clientHeight ?? 1,
  });

  useEffect(() => {
    // The container we are observing may not be mounted by the time this hook is initialized, or
    // it may unmount and remount, both of which cause the initial width to be incorrect until a
    // resize. So we set up an interval that watches for an initial width/height and clears itself
    // once it obtains them.
    if (!initialMountCheckComplete) {
      const containerMountCheckInterval = setInterval(() => {
        if (containerRef.current) {
          clearInterval(containerMountCheckInterval);
          animationFrameRequest.current = requestAnimationFrame(() => {
            if (containerRef.current) {
              setInitialMountCheckComplete(true);
              setContainerSize({
                width: containerRef.current.clientWidth,
                height: containerRef.current.clientHeight,
              });
            }
          });
        }
      }, 50);
    } else if (containerRef.current) {
      const observer = new ResizeObserver(
        debounce(
          (entries) => {
            const contentRect = entries[0]?.contentRect ?? null;
            animationFrameRequest.current = requestAnimationFrame(() =>
              setContainerSize({
                width: contentRect.width,
                height: contentRect.height,
              }),
            );
          },
          50,
          { leading: false, trailing: true },
        ),
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        animationFrameRequest.current && cancelAnimationFrame(animationFrameRequest.current);
        observer.disconnect();
      };
    }

    return () => {
      animationFrameRequest.current && cancelAnimationFrame(animationFrameRequest.current);
    };
  }, [initialMountCheckComplete, containerRef.current]);

  const width = containerSize?.width ?? 0;
  const height = containerSize?.height ?? 0;

  return useMemo(
    () => ({
      width,
      height,
      breakpoint: Object.entries(breakpoints).find(
        ([_breakpoint, value]) => width >= value.min && width <= value.max,
      )?.[0] as Breakpoint,
      breakpoints: {
        isAtLeast: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [name, width >= value.min]),
        ) as Record<Breakpoint, boolean>,
        isAtMost: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [name, width <= value.max]),
        ) as Record<Breakpoint, boolean>,
        is: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [
            name,
            width >= value.min && width <= value.max,
          ]),
        ) as Record<Breakpoint, boolean>,
      },
    }),
    [width, height],
  );
};

interface UseBreakpointQueryResult {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  breakpoints: BreakpointQueries;
}

/*
 * Returns the effective breakpoints based on the current window width.
 */
export const useBreakpointQuery = (): UseBreakpointQueryResult => {
  const animationFrameRequest = useRef<number | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = debounce(
      () => {
        animationFrameRequest.current = requestAnimationFrame(() =>
          setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
          }),
        );
      },
      50,
      { leading: false, trailing: true },
    );

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      animationFrameRequest.current && cancelAnimationFrame(animationFrameRequest.current);
    };
  }, []);

  const width = windowSize.width ?? 0;
  const height = windowSize.height ?? 0;

  return useMemo(
    () => ({
      width,
      height,
      breakpoint: Object.entries(breakpoints).find(
        ([_breakpoint, value]) => width >= value.min && width <= value.max,
      )?.[0] as Breakpoint,
      breakpoints: {
        isAtLeast: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [name, width >= value.min]),
        ) as Record<Breakpoint, boolean>,
        isAtMost: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [name, width <= value.max]),
        ) as Record<Breakpoint, boolean>,
        is: Object.fromEntries(
          Object.entries(breakpoints).map(([name, value]) => [
            name,
            width >= value.min && width <= value.max,
          ]),
        ) as Record<Breakpoint, boolean>,
      },
    }),
    [width, height],
  );
};
